import { useCallback, useMemo } from 'react';

import _findLastIndex from 'lodash/findLastIndex';
import { useSnackbar } from 'notistack';
import { TranslateFunction } from 'react-localize-redux';

import {
  formatNumberToCurrency,
  getBasketTotal,
} from '../../../../utils/basketUtils';
import { checkMenuItemHasAllergens } from '../../../../utils/menuUtils';
import { Track } from '../../../../weborder/analytics';
import {
  Data as Basket,
  Item as BasketItem,
} from '../../../../weborder/BasketControl';
import { Currency } from '../../../api/general/general.types';
import { MenuItem } from '../../../api/menu/menu.types';
import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
} from '../../../api/restaurant/restaurant.types';
import { NutritionInfoDataDictionary } from '../../../state/menu/menu.types';
import {
  AddItemHookPayload,
  AddItemPayloadType,
  RemoveItemHookPayload,
  RemoveItemPayloadType,
  Source,
} from '../../../types/basket.types';
import { ClickItemPayloadType, ItemType } from '../../../types/menu.types';

interface UseBasket {
  handleAddItem: (payload: AddItemHookPayload) => void;
  handleRemoveItem: (payload: RemoveItemHookPayload) => void;
  isTotalOverMinOrder: boolean;
  items: BasketItem[];
  localisedTotal: string;
  total: number;
}

export const useBasket = (
  basket: Basket,
  concessionStoreId: number,
  languageCode: string,
  nutritionInfo: NutritionInfoDataDictionary, // Get from useNutritionInfo hook
  onAddItem: (payload: AddItemPayloadType) => void,
  onRemoveItem: (payload: RemoveItemPayloadType) => void,
  restaurantDetail: CollectionRestaurantDetail | DeliveryRestaurantDetail, // Get from useRestaurantDetails hook
  translate: TranslateFunction,
  onOpenItemDialog?: (payload: ClickItemPayloadType) => void,
): UseBasket => {
  const { enqueueSnackbar } = useSnackbar();

  const { items, menuId } = basket;
  const vrId = restaurantDetail?.VirtualRestaurantId;
  const currency = restaurantDetail?.Currency || Currency.EUR;
  const minimumOrder = restaurantDetail?.MinimumOrder || 0;
  const isRestaurantClosed =
    !!restaurantDetail &&
    !restaurantDetail.IsOpen &&
    !restaurantDetail.AcceptPreOrders;

  const total = useMemo(() => getBasketTotal(items), [menuId, items.length]);

  const localisedTotal = useMemo(
    () => formatNumberToCurrency(total, currency, languageCode),
    [total, currency, languageCode],
  );

  const isTotalOverMinOrder = total >= minimumOrder;

  const displayRestaurantClosedSnackbar = (): void => {
    const {
      OpenTimeMinute: openingTimeMinute,
      OpenTimeHour: openTimeHour,
      RestaurantName: restaurantName,
    } = restaurantDetail;

    const getOpeningTime = (): string => {
      const openTimeMinuteString = `00${openingTimeMinute}`.slice(-2);
      const openTimeHourString = `00${openTimeHour}`.slice(-2);
      return `${openTimeHourString}:${openTimeMinuteString}`;
    };
    const openingTime = getOpeningTime();

    enqueueSnackbar(
      translate('Sorry_{restaurantName}_is_closed_It_will_open_at_{openAt}', {
        restaurantName,
        openingTime,
      }),
      {
        variant: 'warning',
      },
    );
  };

  const getTrackingCategory = (source: Source): string => {
    switch (source) {
      case Source.CheckoutBasket:
      case Source.CheckoutCrossSell:
      case Source.MenuBasket:
        return 'Orders';
      case Source.MenuCards:
      case Source.MenuCrossSellDialog:
      case Source.MenuSearch:
      default:
        return 'Restaurants';
    }
  };

  const trackAddItem = (
    item: MenuItem,
    itemType: ItemType,
    source: Source,
  ): void => {
    const category = getTrackingCategory(source);
    const hasAllergenInfo = checkMenuItemHasAllergens(
      nutritionInfo,
      item.PublicId,
    );

    const analyticsData = {
      amp_descriptionLength: item.Description?.length || 0,
      amp_hasAllergenInfo: hasAllergenInfo,
      amp_hasDescription: !!item.Description,
      amp_hasImage: !!item.ImageUrl,
      amp_itemAddedFrom: source,
      amp_itemCount: 1,
      amp_itemName: item.Name,
      amp_itemPrice: item.Price,
      amp_label: 'Item Added to Basket',
      amp_menuItemId: item.MenuItemId,
      amp_productId: item.MenuItemId.toString(),
      eventLabel: item.Name,
      itemType,
      label: `Add Item To Basket Via ${source}`,
      menuId: item.MenuId,
      name: item.Name,
      section: category,
      virtualRestaurantId: vrId,
    };

    Track(
      category,
      `Add Item To Basket Via ${source}`,
      analyticsData,
      'AddToCart',
    );
  };

  const handleIncrementSimpleItem = (item: MenuItem, source: Source): void => {
    trackAddItem(item, ItemType.Simple, source);
    onAddItem({
      Count: 1,
      Item: item,
      Options: [],
    });
  };

  const handleIncrementComplexItem = (item: MenuItem, source: Source): void => {
    const [lastItemAdded] = items
      .filter((basketItem) => basketItem?.Item?.MenuItemId === item.MenuItemId)
      .slice(-1);

    trackAddItem(item, ItemType.Complex, source);
    onAddItem({
      Item: lastItemAdded.Item,
      Options: lastItemAdded.Options,
    });
  };

  const handleAddItem = useCallback(
    (payload: AddItemHookPayload) => {
      const { item, source } = payload;

      if (isRestaurantClosed) {
        return displayRestaurantClosedSnackbar();
      }

      if (concessionStoreId) {
        Object.assign(item, {
          ConcessionStoreId: concessionStoreId,
        });
      }

      const isSimpleItem = item.MenuItemOptionSets.length === 0;
      if (isSimpleItem) {
        return handleIncrementSimpleItem(item, source);
      }

      const itemQuantity =
        items.filter(
          (basketItem) => basketItem.Item.MenuItemId === item.MenuItemId,
        ).length || 0;
      const isIncrementingComplexItem = itemQuantity > 0;
      if (isIncrementingComplexItem) {
        return handleIncrementComplexItem(item, source);
      }

      if (onOpenItemDialog) {
        return onOpenItemDialog({
          menuId: item.MenuId,
          item,
        });
      }

      return handleIncrementSimpleItem(item, source);
    },
    [concessionStoreId, items.length, vrId],
  );

  const trackRemoveItem = (item: MenuItem, source: Source): void => {
    const category = getTrackingCategory(source);

    const analyticsData = {
      amp_itemName: item.Name,
      amp_itemPrice: 0,
      amp_itemRemovedFrom: source,
      amp_label: 'Item Removed from Basket',
      eventLabel: item.Name,
      label: `Remove Item From Basket Via ${source}`,
      menuId: item.MenuId,
      section: category,
      virtualRestaurantId: vrId,
    };

    Track(category, `Remove Item From Basket Via ${source}`, analyticsData);
  };

  const handleRemoveItem = useCallback(
    (payload: RemoveItemHookPayload) => {
      const { item, source } = payload;

      if (isRestaurantClosed) {
        return displayRestaurantClosedSnackbar();
      }

      const showRemoveItemSnackbar = [
        Source.MenuCards,
        Source.MenuSearch,
      ].includes(source);
      if (showRemoveItemSnackbar) {
        enqueueSnackbar(translate('An_item_was_removed_from_your_basket'), {
          variant: 'warning',
        });
      }

      const lastItemIndex = _findLastIndex(
        items,
        (basketItem) => basketItem.Item.MenuItemId === item.MenuItemId,
      );

      trackRemoveItem(item, source);
      return onRemoveItem({
        index: lastItemIndex,
        indexName: item.Name,
        menuId: item.MenuId,
      });
    },
    [items.length, vrId],
  );

  return {
    handleAddItem,
    handleRemoveItem,
    isTotalOverMinOrder,
    items,
    localisedTotal,
    total,
  };
};
